import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    X, Phone, MapPin, Calendar, Package, User, Clock, Hash,
    Check, Trash2, ChevronRight, Copy, CheckCheck, ArrowRight,
    Loader2, AlertCircle, TrendingUp, RefreshCw as RefreshIcon,
    Edit2, Save, Printer, MessageSquare, FileText, CheckCircle2
} from 'lucide-react';
import { getStatusColor, getStatusLabel, getStatusConfig } from '../../../../config/orderStatus';
import { getHistory, reassignOrder, downloadOrderPdf } from '../../../../services/api/salesOrderApi';
import { getAllUsersWithPagination } from '../../../../services/api/userApi';
import { formatDateTime } from '../../../../utils/dateUtils';
import { normalizeRole } from '../../../../config/roles';

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        minimumFractionDigits: 2
    }).format(amount || 0);

const formatDate = (date, timeSlot) => {
    if (!date) return timeSlot || '—';
    const dateStr = formatDateTime(date, { hour: undefined, minute: undefined });
    return timeSlot ? `${dateStr} · ${timeSlot}` : dateStr;
};

/* ─────────────────────────────────────────────
   Avatar colour (consistent per name)
───────────────────────────────────────────── */
const AVATAR_COLORS = [
    { from: 'from-violet-500', to: 'to-purple-600' },
    { from: 'from-blue-500',   to: 'to-blue-600' },
    { from: 'from-emerald-500', to: 'to-teal-600' },
    { from: 'from-orange-500', to: 'to-amber-600' },
    { from: 'from-rose-500',   to: 'to-pink-600' },
];
const avatarColor = (name = '') => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    const c = AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
    return `${c.from} ${c.to}`;
};

/* ─────────────────────────────────────────────
   Transition button style mapping
───────────────────────────────────────────── */
const transitionStyle = (transition) => {
    const name = (transition.transitionName || transition.label || transition.name || transition.action || '').toLowerCase();
    if (name.includes('cancel')) return { cls: 'border border-red-200 text-red-600 hover:bg-red-50', icon: <Trash2 size={15} /> };
    if (name.includes('reject') || name.includes('return')) return { cls: 'border border-orange-200 text-orange-600 hover:bg-orange-50', icon: <RefreshCw size={15} /> };
    if (name.includes('accept revision') || name.includes('confirm revision') || name.includes('resubmit')) {
        return { cls: 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-md shadow-emerald-200', icon: <Check size={15} /> };
    }
    if (name.includes('revision') || name.includes('revise') || name.includes('edit')) return { cls: 'border border-amber-200 text-amber-700 hover:bg-amber-50', icon: <RefreshCw size={15} /> };
    if (name.includes('cancel')) return { cls: 'border border-red-200 text-red-600 hover:bg-red-50', icon: <Trash2 size={15} /> };
    if (name.includes('approve') || name.includes('resubmit') || name.includes('send') || name.includes('warehouse')) {
        return { cls: 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-md shadow-emerald-200', icon: <Check size={15} /> };
    }
    if (name.includes('dispatch') || name.includes('despatch') || name.includes('deliver')) {
        return { cls: 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-200', icon: <TrendingUp size={15} /> };
    }
    return { cls: 'bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-200', icon: <ArrowRight size={15} /> };
};

/* ─────────────────────────────────────────────
   Status Timeline Item
───────────────────────────────────────────── */
const TimelineItem = ({ event = {}, isLast }) => {
    // Backend returns toStatus/fromStatus as objects { statusCode, label, ... } or plain strings
    const extractCode = (v) => {
        if (!v) return '';
        if (typeof v === 'string') return v;
        if (typeof v === 'object') return v.statusCode || v.code || v.status || '';
        return String(v);
    };
    const statusCode = extractCode(event.toStatus) || extractCode(event.status) ||
                       extractCode(event.toStatusCode) || extractCode(event.fromStatus) || '';
    let cfg;
    try {
        cfg = getStatusConfig(statusCode);
    } catch (_) {
        cfg = { accent: 'bg-gray-400', icon: null };
    }
    const label = getStatusLabel(statusCode) || statusCode || 'Status Update';
    const ts = event.timestamp || event.createdAt || event.changedAt || event.occurredAt || null;
    const actor = event.changedBy || event.actorName || event.performedBy || event.actor || null;
    const notes = event.notes || event.comment || event.remarks || null;


    return (
        <div className="flex gap-3">
            <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ring-4 ring-white flex-shrink-0 ${cfg.accent || 'bg-gray-400'} shadow-sm`}>
                    {cfg.icon ? React.createElement(cfg.icon, { size: 14, className: 'text-white' }) : <Clock size={14} className="text-white" />}
                </div>
                {!isLast && <div className="w-px flex-1 bg-gray-100 my-1" />}
            </div>
            <div className={`pb-5 flex-1 min-w-0`}>
                <p className="text-sm font-bold text-gray-800 leading-tight">
                    {label}
                </p>
                <p className="text-[11px] text-gray-400 font-medium mt-0.5">
                    {ts ? formatDateTime(ts) : '—'}
                    {actor && ` · by ${actor}`}
                </p>
                {notes && (
                    <p className="text-xs text-gray-500 italic mt-1 bg-gray-50 px-2 py-1 rounded-lg">
                        {notes}
                    </p>
                )}
            </div>
        </div>
    );
};


/* ─────────────────────────────────────────────
   RefreshCw mini inline SVG (avoid import clash)
───────────────────────────────────────────── */
const RefreshCw = ({ size, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
        strokeLinejoin="round" className={className}>
        <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
        <path d="M21 3v5h-5" />
    </svg>
);

/* ─────────────────────────────────────────────
   Main Component
───────────────────────────────────────────── */
const OrderDetailDrawer = ({
    order,
    transitions = [],
    onTransition,
    onRecordSale,
    loadingTransition,
    onClose,
    role,
    onOrderUpdate,
    onEditOrder,
}) => {
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(null);
    const [copiedId, setCopiedId] = useState(false);
    
    // Re-assignment state
    const [isEditingSalesPerson, setIsEditingSalesPerson] = useState(false);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [reassignLoading, setReassignLoading] = useState(false);
    const [selectedUserPublicId, setSelectedUserPublicId] = useState('');
    const [selectedDelegatedUserPublicId, setSelectedDelegatedUserPublicId] = useState('');
    const [usersLoading, setUsersLoading] = useState(false);

    const drawerRef = useRef(null);

    /* fetch history when drawer opens */
    useEffect(() => {
        if (!order?.id) return;
        setHistoryLoading(true);
        setHistoryError(null);
        getHistory(order.id)
            .then(data => setHistory(Array.isArray(data) ? data : []))
            .catch(() => setHistoryError('Could not load history.'))
            .finally(() => setHistoryLoading(false));
    }, [order?.id]);

    /* ESC to close */
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    /* lock body scroll */
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    const copyId = useCallback(() => {
        navigator.clipboard.writeText(order?.publicId || order?.id || '').catch(() => {});
        setCopiedId(true);
        setTimeout(() => setCopiedId(false), 2000);
    }, [order]);

    const handleReassign = async () => {
        setReassignLoading(true);
        try {
            const updatedOrder = await reassignOrder(order.publicId || order.id, {
                assignedSalesPersonPublicId: selectedUserPublicId || null,
                delegatedSalesPersonPublicId: selectedDelegatedUserPublicId || 'none',
                notes: `Sales team reassigned/delegated`
            });
            
            setIsEditingSalesPerson(false);
            if (onOrderUpdate) {
                onOrderUpdate(updatedOrder);
            }
            
            // Refresh history
            getHistory(order.id).then(data => setHistory(Array.isArray(data) ? data : []));
        } catch (error) {
            console.error('Failed to reassign:', error);
            alert('Failed to reassign order: ' + (error.message || 'Error occurred'));
        } finally {
            setReassignLoading(false);
        }
    };

    const fetchUsers = async () => {
        setUsersLoading(true);
        try {
            const data = await getAllUsersWithPagination({ size: 100 });
            setAvailableUsers(data.content || []);
            // Pre-select current
            setSelectedUserPublicId(order.assignedSalesPersonPublicId || '');
            setSelectedDelegatedUserPublicId(order.delegatedSalesPersonPublicId || '');
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setUsersLoading(false);
        }
    };

    const startEditing = () => {
        setIsEditingSalesPerson(true);
        if (availableUsers.length === 0) {
            fetchUsers();
        }
    };

    if (!order) return null;

    const items = order.items || [];
    const totalAmount = order.totalAmount || items.reduce((s, i) => s + (i.total || 0), 0);


    return (
        <>
            {/* ── Backdrop ── */}
            <div
                className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-[200] transition-opacity duration-300"
                onClick={onClose}
                aria-label="Close order details"
            />

            {/* ── Drawer panel ── */}
            <div
                ref={drawerRef}
                className="fixed right-0 top-0 h-[100dvh] w-full max-w-xl bg-white z-[201] flex flex-col shadow-[−8px_0_40px_rgba(0,0,0,0.12)] animate-slideInRight"
                role="dialog"
                aria-modal="true"
                aria-label="Order details"
            >
                {/* ── Sticky Header ── */}
                <div className="flex-shrink-0 px-6 py-4 border-b border-gray-100 bg-white">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h2 className="text-xl font-black text-gray-900 tracking-tight">
                                    {order.orderRef || `#${(order.id || '').slice(-8)}`}
                                </h2>
                                <span className={`px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${getStatusColor(order.status)}`}>
                                    {getStatusLabel(order.status)}
                                </span>
                            </div>

                            <div className="flex items-center gap-2 mt-1.5">
                                <span className="text-[11px] font-black text-blue-600 font-mono bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100 truncate max-w-[260px]">
                                    {order.publicId || order.id}
                                </span>
                                <button
                                    onClick={copyId}
                                    className="flex items-center gap-1 text-[10px] font-bold text-gray-400 hover:text-blue-600 transition-colors"
                                    title="Copy ID"
                                >
                                    {copiedId ? <CheckCheck size={13} className="text-emerald-500" /> : <Copy size={13} />}
                                    {copiedId ? 'Copied' : 'Copy'}
                                </button>

                                <div className="w-px h-3 bg-gray-200 mx-1" />

                                <button
                                    onClick={() => downloadOrderPdf(order.publicId || order.id)}
                                    className="flex items-center gap-1 text-[10px] font-bold text-gray-400 hover:text-indigo-600 transition-colors"
                                    title="Print Invoice"
                                >
                                    <Printer size={13} />
                                    Print PDF
                                </button>
                            </div>

                            {order.createdAt && (
                                <div className="flex flex-col gap-0.5 mt-1">
                                    <p className="text-[11px] text-gray-400 font-medium flex items-center gap-1">
                                        <Clock size={11} />
                                        Placed {formatDateTime(order.createdAt)}
                                    </p>
                                    {order.updatedAt && order.updatedAt !== order.createdAt && (
                                        <p className="text-[11px] text-blue-500 font-bold flex items-center gap-1">
                                            <RefreshIcon size={11} />
                                            Last Updated {formatDateTime(order.updatedAt)}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        <button
                            onClick={onClose}
                            className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all flex-shrink-0"
                            aria-label="Close"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* ── Scrollable Body ── */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">

                    {/* ── Customer Info ── */}
                    <div className="px-6 pt-5 pb-4 border-b border-gray-50">
                        <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${avatarColor(order.customer?.name)} flex items-center justify-center text-white font-bold text-lg shadow-md ring-4 ring-white flex-shrink-0`}>
                                {(order.customer?.name || 'U')[0].toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0 pt-0.5">
                                <h3 className="font-bold text-gray-900 text-base leading-tight">
                                    {order.customer?.name || '—'}
                                </h3>
                                 {order.customerSalesman && (
                                    <p className="text-[10px] text-gray-400 font-medium flex items-center gap-1 mt-0.5" title="Customer's default sales person">
                                        <User size={10} className="text-gray-300" />
                                        {order.customerSalesman} (Customer Rep)
                                    </p>
                                )}

                                {!isEditingSalesPerson && (
                                    <div className="space-y-1 mt-1">
                                        <div className="flex items-center justify-between">
                                            <p className="text-xs text-blue-600 font-bold flex items-center gap-1">
                                                <User size={11} className="text-blue-500" />
                                                Assigned: {order.salesman || 'Unassigned'} {order.orderSalesman ? '(Order Level)' : ''}
                                            </p>
                                             {(() => {
                                                 const userRole = normalizeRole(role) || role;
                                                 if (['TENANT_ADMIN', 'TENANT_OWNER', 'SALES_MANAGER', 'SYSTEM_ADMIN'].includes(userRole)) {
                                                     return (
                                                         <button 
                                                             onClick={startEditing}
                                                             className="p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-blue-600 transition-all flex items-center gap-1 text-[11px] font-semibold text-blue-600"
                                                             title="Re-assign / Delegate order"
                                                         >
                                                             <Edit2 size={12} />
                                                             <span>Re-assign / Delegate</span>
                                                         </button>
                                                     );
                                                 }
                                                 return null;
                                             })()}
                                        </div>
                                        {order.delegatedSalesman && (
                                            <p className="text-xs text-purple-600 font-bold flex items-center gap-1">
                                                <User size={11} className="text-purple-500" />
                                                Delegated: {order.delegatedSalesman}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {isEditingSalesPerson && (
                                    <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
                                        <div>
                                            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                                                Assigned Sales Rep
                                            </label>
                                            <div className="relative">
                                                <select
                                                    value={selectedUserPublicId}
                                                    onChange={(e) => setSelectedUserPublicId(e.target.value)}
                                                    disabled={reassignLoading || usersLoading}
                                                    className="w-full text-xs border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 bg-white appearance-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all disabled:bg-gray-50 font-medium"
                                                >
                                                    <option value="">-- Select Primary Sales Person --</option>
                                                    {availableUsers.map(u => (
                                                        <option key={u.publicId} value={u.publicId}>
                                                            {u.firstName} {u.lastName} ({u.username})
                                                        </option>
                                                    ))}
                                                </select>
                                                <User className="absolute left-2.5 top-1/2 -translate-y-1/2 text-blue-500" size={12} />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                                                Delegated Sales Rep (Optional)
                                            </label>
                                            <div className="relative">
                                                <select
                                                    value={selectedDelegatedUserPublicId}
                                                    onChange={(e) => setSelectedDelegatedUserPublicId(e.target.value)}
                                                    disabled={reassignLoading || usersLoading}
                                                    className="w-full text-xs border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 bg-white appearance-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all disabled:bg-gray-50 font-medium text-purple-700"
                                                >
                                                    <option value="">-- No Delegated Sales Person --</option>
                                                    {availableUsers.map(u => (
                                                        <option key={u.publicId} value={u.publicId}>
                                                            {u.firstName} {u.lastName} ({u.username})
                                                        </option>
                                                    ))}
                                                </select>
                                                <User className="absolute left-2.5 top-1/2 -translate-y-1/2 text-purple-500" size={12} />
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-end gap-2 pt-1 border-t border-gray-200">
                                            <button
                                                onClick={() => setIsEditingSalesPerson(false)}
                                                disabled={reassignLoading}
                                                className="px-3 py-1.5 bg-white text-gray-600 text-xs font-bold rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleReassign}
                                                disabled={reassignLoading || usersLoading}
                                                className="px-3 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50 shadow-sm flex items-center gap-1.5 cursor-pointer"
                                            >
                                                {reassignLoading ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                                                <span>Save Staff Assignment</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-2 mt-4">
                            {order.customer?.phone && (
                                <div className="flex items-center gap-2.5 text-sm text-gray-600">
                                    <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                                        <Phone size={13} className="text-blue-500" />
                                    </div>
                                    <span className="font-medium">{order.customer.phone}</span>
                                </div>
                            )}
                            {order.customer?.address && (
                                <div className="flex items-start gap-2.5 text-sm text-gray-600">
                                    <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <MapPin size={13} className="text-orange-500" />
                                    </div>
                                    <span className="text-gray-500 leading-snug">{order.customer.address}</span>
                                </div>
                            )}
                            {(order.deliveryDate || order.timeSlot) && (
                                <div className="flex items-center gap-2.5 text-sm text-gray-600">
                                    <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                                        <Calendar size={13} className="text-blue-500" />
                                    </div>
                                    <span className="font-bold text-gray-700">{formatDate(order.deliveryDate, order.timeSlot)}</span>
                                </div>
                            )}
                            {order.notes && (
                                <div className="flex items-start gap-2.5 text-sm text-gray-600 mt-1">
                                    <div className="w-7 h-7 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <MessageSquare size={13} className="text-purple-500" />
                                    </div>
                                    <div className="flex-1">
                                        <span className="text-xs font-black uppercase tracking-widest text-gray-400 block">Special Instructions</span>
                                        <span className="text-gray-700 leading-snug break-words">{order.notes}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Order Items ── */}
                    <div className="px-6 py-5 border-b border-gray-50">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 flex items-center gap-2">
                                <Package size={13} />
                                Order Items
                            </h4>
                            <span className="text-xs font-black text-blue-700 bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-100">
                                {items.length} {items.length === 1 ? 'Item' : 'Items'}
                            </span>
                        </div>

                        {items.length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-4">No items</p>
                        ) : (
                            <div className="rounded-2xl border border-gray-100 overflow-hidden">
                                {/* Table header */}
                                <div className="grid grid-cols-[1fr_auto_auto] gap-2 px-4 py-2 bg-gray-50 border-b border-gray-100">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Product</span>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 text-right">Qty</span>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 text-right">Amount</span>
                                </div>
                                {/* Rows */}
                                {items.map((item, idx) => {
                                    const hasShortage = item.warehouseQuantity !== null && 
                                                      item.warehouseQuantity !== undefined && 
                                                      Number(item.warehouseQuantity) < Number(item.quantity);
                                    const hasRevision = item.warehouseQuantity !== null && 
                                                       item.warehouseQuantity !== undefined && 
                                                       Number(item.warehouseQuantity) !== Number(item.quantity);
                                                       
                                    return (
                                        <div
                                            key={item.id || idx}
                                            className={`grid grid-cols-[1fr_auto_auto] gap-2 px-4 py-3 items-center ${idx < items.length - 1 ? 'border-b border-gray-50' : ''} ${hasShortage ? 'bg-rose-50/30' : hasRevision ? 'bg-amber-50/30' : ''}`}
                                        >
                                            <div className="min-w-0">
                                                <p className="text-sm font-bold text-gray-800 leading-snug" title={item.name}>
                                                    {item.name}
                                                </p>
                                                {item.price > 0 && (
                                                    <p className="text-[11px] text-gray-400 font-medium mt-0.5">
                                                        {formatCurrency(item.price)} / {item.unit || 'pcs'}
                                                    </p>
                                                )}
                                                {hasRevision && (
                                                    <div className={`mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg border text-[10px] font-black uppercase tracking-tighter ${hasShortage ? 'bg-rose-100 border-rose-200 text-rose-700' : 'bg-amber-100 border-amber-200 text-amber-700'}`}>
                                                        <AlertCircle size={10} />
                                                        Warehouse suggested: {item.warehouseQuantity}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <span className={`text-sm font-bold block ${hasRevision ? 'text-gray-400 line-through' : 'text-gray-600'}`}>
                                                    {item.quantity} {item.unit || 'pcs'}
                                                </span>
                                                {hasRevision && (
                                                    <span className={`text-sm font-black block ${hasShortage ? 'text-rose-600' : 'text-amber-600'}`}>
                                                        {item.warehouseQuantity} {item.unit || 'pcs'}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="text-sm font-bold text-gray-900 text-right whitespace-nowrap">
                                                {formatCurrency((hasRevision ? item.warehouseQuantity : item.quantity) * (item.price || 0))}
                                            </span>
                                        </div>
                                    );
                                })}
                                {/* Total row */}
                                {(() => {
                                    const subtotalExVat = items.reduce((sum, item) => sum + (item.total || 0), 0);
                                    const grandTotal = totalAmount || subtotalExVat;
                                    const totalVat = Math.max(0, grandTotal - subtotalExVat);
                                    return (
                                        <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3.5 space-y-2">
                                            <div className="flex justify-between items-center text-xs text-gray-500 font-bold">
                                                <span>Subtotal (Excl. VAT)</span>
                                                <span>{formatCurrency(subtotalExVat)}</span>
                                            </div>
                                            <div className="flex justify-between items-center text-xs text-orange-500 font-bold">
                                                <span>VAT (5%)</span>
                                                <span>{formatCurrency(totalVat)}</span>
                                            </div>
                                            <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                                                <span className="text-xs font-black uppercase tracking-widest text-gray-400">Total (Incl. VAT)</span>
                                                <span className="text-xl font-black text-blue-600">{formatCurrency(grandTotal)}</span>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                    </div>

                    {/* ── Status History ── */}
                    <div className="px-6 py-5">
                        <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 flex items-center gap-2 mb-4">
                            <Clock size={13} />
                            Status History
                        </h4>

                        {historyLoading && (
                            <div className="flex items-center gap-3 text-sm text-gray-400 py-4">
                                <Loader2 size={16} className="animate-spin" />
                                Loading history...
                            </div>
                        )}

                        {historyError && !historyLoading && (
                            <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 rounded-xl px-3 py-2">
                                <AlertCircle size={14} />
                                {historyError}
                            </div>
                        )}

                        {!historyLoading && !historyError && history.length === 0 && (
                            <p className="text-sm text-gray-400 py-2">No history recorded yet.</p>
                        )}

                        {!historyLoading && history.length > 0 && (
                            <div>
                                {history.map((event, idx) => (
                                    <TimelineItem
                                        key={idx}
                                        event={event}
                                        isLast={idx === history.length - 1}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Sticky Actions Footer ── */}
                {transitions.length > 0 && (
                    <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4 bg-white">
                        {/* Validation warning if modification required but not edited yet */}
                        {order.status === 'REQUEST_FOR_MODIFICATION' && items.some(i => i.warehouseQuantity !== null && i.warehouseQuantity !== undefined && Number(i.warehouseQuantity) !== Number(i.quantity)) && (
                            <div className="mb-4 p-3 bg-amber-50 border border-amber-100 rounded-xl flex items-start gap-2.5 text-amber-700 text-xs font-semibold animate-in fade-in duration-200">
                                <AlertCircle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-black uppercase tracking-wider text-[9px]">Edit Required</p>
                                    <p className="font-bold text-amber-600 mt-0.5">Please edit this order to address the warehouse suggested modifications before resubmitting.</p>
                                </div>
                            </div>
                        )}
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-3 text-right">
                            Available Actions
                        </p>
                        <div className="flex flex-wrap gap-2 justify-end">
                            {/* Render Edit Order button directly inside the actions list if modification is required and not edited yet */}
                            {order.status === 'REQUEST_FOR_MODIFICATION' && items.some(i => i.warehouseQuantity !== null && i.warehouseQuantity !== undefined && Number(i.warehouseQuantity) !== Number(i.quantity)) && onEditOrder && (
                                <button
                                    type="button"
                                    onClick={() => onEditOrder(order)}
                                    className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-[11px] font-bold bg-blue-600 text-white hover:bg-blue-700 transition-all duration-200 active:scale-95 shadow-md shadow-blue-200"
                                >
                                    <Edit2 size={14} />
                                    Edit Order
                                </button>
                            )}

                            {transitions.reduce((uniq, t) => {
                                const getCanonicalId = (item) => {
                                    const raw = (item.transitionName || item.label || item.name || item.action || '').toLowerCase();
                                    if (raw.includes('deliver')) return 'deliver';
                                    if (raw.includes('warehouse')) return 'warehouse';
                                    if (raw.includes('cancel')) return 'cancel';
                                    if (raw.includes('reject')) return 'reject';
                                    if (raw.includes('return')) return 'return';
                                    if (raw.includes('approve')) return 'approve';
                                    if (raw.includes('ready')) return 'ready';
                                    return raw.replace(/\bas\b/g, '').trim();
                                };
                                if (!uniq.some(ext => getCanonicalId(ext) === getCanonicalId(t))) {
                                    uniq.push(t);
                                }
                                return uniq;
                            }, []).map((t) => {
                                const { cls, icon } = transitionStyle(t);
                                const rawLabel = t.transitionName || t.label || t.name || t.action || 'Action';
                                
                                // Hide Cancel transition option for warehouse staff
                                const userRole = normalizeRole(role) || role;
                                if (userRole === 'WAREHOUSE_PERSON' && rawLabel.toLowerCase().includes('cancel')) {
                                    return null;
                                }

                                let cleanLabel = rawLabel;
                                if (rawLabel.toLowerCase().includes('send to warehouse')) cleanLabel = 'To Warehouse';
                                if (rawLabel.toLowerCase().includes('deliver')) cleanLabel = 'Deliver';
                                
                                const isResubmit = rawLabel.toLowerCase().includes('accept revision') || rawLabel.toLowerCase().includes('confirm revision') || rawLabel.toLowerCase().includes('resubmit');
                                if (isResubmit) cleanLabel = 'Resubmit';

                                const isLoading = loadingTransition;
                                const isModificationRequired = order.status === 'REQUEST_FOR_MODIFICATION';
                                const hasAnyRevision = items.some(i => i.warehouseQuantity !== null && i.warehouseQuantity !== undefined && Number(i.warehouseQuantity) !== Number(i.quantity));
                                const isDisabled = !!isLoading || (isResubmit && isModificationRequired && hasAnyRevision);
                                
                                if (isResubmit && isModificationRequired && hasAnyRevision && !onEditOrder) {
                                    return null;
                                }

                                const isDeliver = cleanLabel === 'Deliver' || rawLabel.toLowerCase().includes('deliver');

                                return (
                                    <React.Fragment key={t.publicId || t.id || cleanLabel}>
                                        <button
                                            onClick={() => {
                                                onTransition(order, t);
                                            }}
                                            disabled={isDisabled}
                                            title={isDisabled && isResubmit ? "Address modifications by editing the order first" : cleanLabel}
                                            className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-[11px] whitespace-nowrap font-bold transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${cls}`}
                                        >
                                            {isLoading ? (
                                                <Loader2 size={14} className="animate-spin" />
                                            ) : icon}
                                            {cleanLabel}
                                        </button>
                                        {isDeliver && onRecordSale && (
                                            <button
                                                type="button"
                                                onClick={() => onRecordSale(order)}
                                                disabled={isDisabled}
                                                className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-[11px] whitespace-nowrap font-bold border shadow-sm transition-all duration-200 active:scale-95 disabled:opacity-50 ${order.recordedSale ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100' : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50'}`}
                                            >
                                                {order.recordedSale ? <CheckCircle2 size={14} className="text-emerald-600" /> : <FileText size={14} />}
                                                {order.recordedSale ? 'Recorded (Edit)' : 'Record Sale'}
                                            </button>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes slideInRight {
                    from { transform: translateX(100%); opacity: 0; }
                    to   { transform: translateX(0);    opacity: 1; }
                }
                .animate-slideInRight {
                    animation: slideInRight 0.28s cubic-bezier(0.22, 1, 0.36, 1) both;
                }
            `}</style>
        </>
    );
};

export default OrderDetailDrawer;
